import { authService } from './auth';
import { billingApi } from './billing.api';
import { ApiError } from './core.api';
import { t } from './i18n';
import { onboardingApi } from './onboarding.api';
import { toastService } from './toast';

const PUBLIC_PATHS = new Set(['/', '/login/', '/pricing/', '/help/', '/survey/', '/welcome/', '/property/', '/tenant/invite/']);
const RESTRICTED_ALLOWED_PATH = '/profile/';
const TENANT_ALLOWED_PATHS = new Set([
  '/profile/',
  '/onboarding/',
  '/tenant/active/',
  '/tenant/upcoming/',
  '/tenant/applications/',
  '/tenant/history/',
]);
const ACCESS_GUARD_WARNING_KEY = 'rc_access_guard_warning';

function getCurrentPath(): string {
  return window.location.pathname;
}

function isRestrictedSubscriptionPage(): boolean {
  return getCurrentPath() === '/profile/' && new URLSearchParams(window.location.search).get('tab') === 'subscription';
}

function isLoginPage(): boolean {
  return getCurrentPath() === '/login/';
}

function isOnboardingPage(): boolean {
  return getCurrentPath() === '/onboarding/';
}

export function shouldBypassOnboardingRedirect(pathname: string, search: string, hasPendingCheckout: boolean): boolean {
  return false;
}

function isPricingCheckoutResume(pathname: string, search: string, hasPendingCheckout: boolean): boolean {
  if (!hasPendingCheckout || pathname !== '/pricing/') {
    return false;
  }

  const params = new URLSearchParams(search);
  return params.get('checkout') === 'resume';
}

function isPublicPath(): boolean {
  return PUBLIC_PATHS.has(getCurrentPath());
}

function isProtectedPath(): boolean {
  return !isPublicPath();
}

function isTenantAllowedPath(): boolean {
  return TENANT_ALLOWED_PATHS.has(getCurrentPath()) || isPublicPath();
}

function showAccessCheckWarning(): void {
  const warningKey = `${ACCESS_GUARD_WARNING_KEY}:${window.location.pathname}${window.location.search}`;
  if (sessionStorage.getItem(warningKey) === '1') {
    return;
  }

  sessionStorage.setItem(warningKey, '1');
  toastService.warning(t('accessGuard.checkFailed'), 6000);
}

export async function enforceAuthenticatedAccess(): Promise<void> {
  if (!authService.isAuthenticated) {
    if (isProtectedPath()) {
      authService.rememberPostLoginRedirect(`${window.location.pathname}${window.location.search}`);
      window.location.href = '/login/';
    }
    return;
  }

  if (authService.isAdmin) {
    if (isLoginPage()) {
      authService.redirectAuthenticatedUser(authService.getDefaultAuthenticatedPath());
    }
    return;
  }

  if (!authService.isAuthenticated) {
    return;
  }

  const currentUser = authService.currentUser;
  if (currentUser?.accountMode === 'tenant') {
    if (isLoginPage()) {
      authService.redirectAuthenticatedUser(authService.getDefaultAuthenticatedPath());
      return;
    }

    if (!isTenantAllowedPath()) {
      window.location.href = '/tenant/active/';
      return;
    }

    if (!isOnboardingPage() && !currentUser.onboardingCompleted) {
      authService.rememberPostOnboardingRedirect(`${window.location.pathname}${window.location.search}`);
      window.location.href = '/onboarding/';
    }

    return;
  }

  try {
    const overview = await billingApi.getOverview();
    const shouldDeferOnboardingRedirect = shouldBypassOnboardingRedirect(
      window.location.pathname,
      window.location.search,
      billingApi.getPendingCheckout() !== null,
    );

    if (!overview.hasApplicationAccess) {
      if (!isRestrictedSubscriptionPage()) {
        window.location.href = '/profile/?tab=subscription';
      }
      return;
    }

    if (isLoginPage()) {
      authService.redirectAuthenticatedUser(authService.getDefaultAuthenticatedPath());
      return;
    }

    if (!isOnboardingPage() && !shouldDeferOnboardingRedirect) {
      const onboardingStatus = await onboardingApi.getStatus();
      if (!onboardingStatus.isCompleted) {
        if (isPricingCheckoutResume(window.location.pathname, window.location.search, billingApi.getPendingCheckout() !== null)) {
          authService.rememberPostOnboardingRedirect(`${window.location.pathname}${window.location.search}`);
        }

        window.location.href = '/onboarding/';
      }
    }
  } catch (error) {
    if (error instanceof ApiError && error.code === 'trial_expired') {
      if (getCurrentPath() !== RESTRICTED_ALLOWED_PATH && !isPublicPath()) {
        window.location.href = '/profile/?tab=subscription';
      }
      return;
    }

    if (error instanceof ApiError && error.code === 'tenant_mode_access_denied') {
      window.location.href = '/tenant/active/';
      return;
    }

    if (isLoginPage()) {
      authService.redirectAuthenticatedUser(authService.getDefaultAuthenticatedPath());
      return;
    }

    if (isProtectedPath()) {
      showAccessCheckWarning();
    }

    if (process.env.RC_TEST_VERBOSE) {
      console.error('Access guard verification failed:', error);
    }
  }
}

if (typeof window !== 'undefined') {
  const warningPrefix = `${ACCESS_GUARD_WARNING_KEY}:`;
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith(warningPrefix))
    .forEach((key) => {
      if (key !== `${warningPrefix}${window.location.pathname}${window.location.search}`) {
        sessionStorage.removeItem(key);
      }
    });

  void enforceAuthenticatedAccess();
}
